/**
 * Wrapper around TwTripTooltipContent that fetches the trip data and passes forward.
 * Also shows a loading spinner while fetching the data.
 * */

import React, { FC, useEffect, useRef, useState } from 'react';
import { TwTripTooltipInfoProps } from './TwLoadTooltipInfo';
import { useLazyGetTripByIdQuery } from '../../redux/api/trips/tripApi';
import { TRIP_STATUS } from '../../redux/api/trips/trips.model';
import DateUtils from '../../utils/dateUtils';
import TwTripTooltipContent from './TwTripTooltipContent';
import useAuth from '../../providers/Auth/Auth.provider';
import { ISearchTagsResponse } from '../../redux/api/loadTags/loadTags.model';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import { Order } from '../../redux/api/orders/orders.model';

export interface TooltipOrderInfo {
  orderReadableId: number | string;
  clientName?: string;
  tripsLength: number;
  tags: ISearchTagsResponse[];
}

interface TwTripTooltipContentByIdProps {
  tripId?: string;
  orderInfo?: TooltipOrderInfo;
}

const TwTripTooltipContentById: FC<TwTripTooltipContentByIdProps> = ({ tripId, orderInfo }) => {
  const {
    getTenantSetting,
    data: { userData }
  } = useAuth();
  const use24HourClock = getTenantSetting('use-24-hour-clock', false);
  const enableLtl = userData?.tenant?.ltl_enabled || false;

  const [loading, setLoading] = useState(false);
  const [tripContentData, setTripContentData] = useState<TwTripTooltipInfoProps>();

  const [getTrip] = useLazyGetTripByIdQuery();

  const showCustomersName = (orders: Order[] | undefined): string => {
    if (orderInfo && orderInfo.clientName) {
      return orderInfo.clientName;
    } else if (orders && orders.length > 0) {
      if (!enableLtl) {
        return orders[0].raw_customer?.name || '';
      } else {
        const totalOrders = orders.length;
        let customerNames = '';

        if (totalOrders > 1) {
          customerNames = orders[0].raw_customer?.name + ' + ' + (totalOrders - 1);
        } else {
          customerNames = orders[0].raw_customer?.name || '';
        }

        return customerNames;
      }
    }

    return '';
  };

  const showOrderReadableId = (orders: Order[] | undefined): string => {
    if (orderInfo && orderInfo.orderReadableId) {
      return orderInfo.orderReadableId.toString();
    } else if (!enableLtl && orders && orders.length === 1) {
      return orders[0].readable_id.toString();
    } else if (enableLtl && orders && orders.length > 0) {
      let orderIds = '';
      orders.forEach((order, index) => {
        orderIds += order.readable_id;
        if (index < orders.length - 1) {
          orderIds += ', ';
        }
      });

      return orderIds;
    }

    return '';
  };

  const fetchAndSetTrip = async (tripId: string): Promise<void> => {
    try {
      const trip = await getTrip(tripId).unwrap();
      const pickup = trip?.stops ? trip.stops[0] : null;
      const delivery = trip?.stops ? trip.stops[trip.stops.length - 1] : null;
      const tripData: TwTripTooltipInfoProps = {
        loadId: '',
        tripId: trip.id,
        tags: trip.tags || [],
        tripStatus: trip.status || TRIP_STATUS.DRAFT,
        clientName: showCustomersName(trip.orders),
        pickupLocation: trip.stops ? trip.stops[0].location : null,
        deliveryLocation: trip.stops ? trip.stops[trip.stops.length - 1].location : null,
        stops: trip.stops ? trip.stops.length - 2 : 0,
        pickupDate:
          pickup && pickup.start_date
            ? DateUtils.getMediumDate(new Date(pickup.start_date)).split(',')[0]
            : '',
        pickupStartTime: pickup
          ? DateUtils.formatTimeForTooltip(use24HourClock, pickup.start_time, pickup.start_date)
          : '',
        pickupEndTime: pickup
          ? DateUtils.formatTimeForTooltip(use24HourClock, pickup.end_time, pickup.start_date)
          : '',
        deliveryDate:
          delivery && delivery.end_date
            ? DateUtils.getMediumDate(new Date(delivery?.end_date)).split(',')[0]
            : '',
        deliveryStartTime: delivery
          ? DateUtils.formatTimeForTooltip(use24HourClock, delivery.start_time, delivery.end_date)
          : '',
        deliveryEndTime: delivery
          ? DateUtils.formatTimeForTooltip(use24HourClock, delivery.end_time, delivery.end_date)
          : '',
        unassigned: !!(trip.drivers && trip.drivers.length === 0),
        loadReadableId: showOrderReadableId(trip.orders),
        pickupNumber:
          trip.stops && trip.stops.length > 0 ? trip.stops[0].stop_identifier || '' : '',
        deliveryNumber:
          trip.stops && trip.stops.length > 0
            ? trip.stops[trip.stops.length - 1].stop_identifier || ''
            : '',
        segmentsCount: orderInfo?.tripsLength || 0,
        order: trip.sort_order || 0,
        driverName:
          trip.drivers && trip.drivers.length > 0
            ? `${trip.drivers[0]?.first_name} ${trip.drivers[0].last_name}`
            : '',
        truckName: trip.truck?.name || '',
        trailerName: trip.trailer?.name || ''
      };
      setTripContentData(tripData);
    } catch (error) {
      console.error('Error getting trip info', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tripId) {
      setLoading(true);
      fetchAndSetTrip(tripId);
    }
  }, [tripId]);

  return (
    <>
      {loading && !tripContentData && (
        <Stack
          flexDirection={'row'}
          justifyContent={'center'}
          sx={{ width: '50px', padding: '10px' }}
        >
          <CircularProgress size={20} />
        </Stack>
      )}
      {!loading && tripContentData && <TwTripTooltipContent tripData={tripContentData} />}
    </>
  );
};

export default TwTripTooltipContentById;
